import type * as api from '@gitbook/api-client';
import { intlPluralizeString } from '@gitbook/react-uikit';
import { confirm, toast } from '@gitbook/spine-react';
import { useEventCallback } from '@gitbook/util-react-hooks';

import { apiClient } from '../../../state';
import { SiteStructureItemList } from './SiteStructureItemList';

export interface UseDeleteSiteItemsProps {
    organization: api.Organization;
    site: api.Site;
}

export function useDeleteSiteItems(props: UseDeleteSiteItemsProps) {
    const { organization, site } = props;

    return useEventCallback((items: (api.SiteSectionGroup | api.SiteSection | api.SiteSpace)[]) => {
        const siteSectionGroups = items.filter(
            (item): item is api.SiteSectionGroup => item.object === 'site-section-group'
        );
        const siteSections = items.filter(
            (item): item is api.SiteSection => item.object === 'site-section'
        );
        const siteSpaces = items.filter(
            (item): item is api.SiteSpace => item.object === 'site-space'
        );

        function getTitleAndDescription() {
            if (
                siteSectionGroups.length > 0 &&
                siteSections.length === 0 &&
                siteSpaces.length === 0
            ) {
                const pluralizedGroup = intlPluralizeString({
                    count: siteSectionGroups.length,
                    singular: 'group',
                    plural: 'groups',
                });
                return {
                    title: `Remove ${pluralizedGroup}?`,
                    description: `This will remove the ${pluralizedGroup} but any sections will remain in the site.`,
                    successToast: `The ${pluralizedGroup} have been removed`,
                    failureToast: `Error while removing ${pluralizedGroup}`,
                };
            }
            if (
                siteSectionGroups.length === 0 &&
                siteSections.length > 0 &&
                siteSpaces.length === 0
            ) {
                const pluralizedSection = intlPluralizeString({
                    count: siteSections.length,
                    singular: 'section',
                    plural: 'sections',
                });
                return {
                    title: `Remove ${pluralizedSection}?`,
                    description: `The ${pluralizedSection} and its variants will be removed from this site.`,
                    successToast: `The ${pluralizedSection} have been removed`,
                    failureToast: `Error while removing ${pluralizedSection}`,
                };
            }
            if (
                siteSectionGroups.length === 0 &&
                siteSections.length === 0 &&
                siteSpaces.length > 0
            ) {
                const pluralizedVariant = intlPluralizeString({
                    count: siteSpaces.length,
                    singular: 'variant',
                    plural: 'variants',
                });
                return {
                    title: `Remove ${pluralizedVariant}?`,
                    description: `The ${pluralizedVariant} will be removed from this site.`,
                    successToast: `The ${pluralizedVariant} have been removed`,
                    failureToast: `Error while removing ${pluralizedVariant}`,
                };
            }

            const total = siteSectionGroups.length + siteSections.length + siteSpaces.length;

            const pluralizedItems = intlPluralizeString({
                count: total,
                singular: 'item',
                plural: 'items',
            });

            return {
                title: `Remove ${pluralizedItems}?`,
                description: `The ${pluralizedItems} will be removed from this site.`,
                successToast: `The ${pluralizedItems} have been removed`,
                failureToast: `Error while removing ${pluralizedItems}`,
            };
        }

        const { title, description, successToast, failureToast } = getTitleAndDescription();

        void confirm({
            title,
            description,
            confirmLabel: 'Remove',
            children: <SiteStructureItemList items={items} />,
            kind: 'danger',
            onConfirm: async (_event, { preventClose }) => {
                await toast.catch(
                    async () => {
                        await Promise.all(
                            siteSpaces.map((siteSpace) =>
                                apiClient.siteSpaces.deleteSiteSpaceById(
                                    organization.id,
                                    site.id,
                                    siteSpace.id
                                )
                            )
                        );
                        await Promise.all(
                            siteSections.map((section) =>
                                apiClient.siteSections.deleteSiteSectionById(
                                    organization.id,
                                    site.id,
                                    section.id
                                )
                            )
                        );
                        await Promise.all(
                            siteSectionGroups.map((group) =>
                                apiClient.siteSectionGroups.deleteSiteSectionGroupById(
                                    organization.id,
                                    site.id,
                                    group.id
                                )
                            )
                        );
                        toast.success(successToast);
                    },
                    failureToast,
                    { onReject: preventClose }
                );
            },
        });
        return;
    });
}
